import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useEffect, useCallback, useState, useMemo } from 'react';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import {
  saveBrochureRequest,
  sendBrochureRequest,
  updateBrochureField,
  updateBrochureFields,
  updateStoredBrochureFields,
} from '../../../../../store/slices/brochure';
import { SearchStatus, TravelTypes, BrochureTemplateId } from '../../../../../constants';
import EmailToClientForm from '../../../../EmailToClientForm';
import Checkbox from '../../../../Checkbox';
import ConfirmModal from '../../../../ConfirmModal';
import PaperTemplateBanner from './PaperTemplateBanner';
import removeFlightSelectionParams from '../../../../../utils/removeFlightSelectionParams';

const BrochureEmailForm = ({ tripId, id }) => {
  const { email, name, travelAgency } = useSelector((store) => store.user);
  const { brochure } = useSelector((state) => state.brochure);
  const { saveBrochureStatus, saveBrochureError, sendBrochureStatus, sendBrochureError } = brochure;
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const router = useRouter();
  const { brochureId: brochureIdFromUrl } = router?.query ?? {};
  const brochureSettings =
    (travelAgency && travelAgency.settings && travelAgency.settings.brochureSettings) || {};
  const displayLabel = (brochureSettings.DisplayName || 'brochure').toLowerCase();
  const doHideSentCopyToMe = !!brochureSettings.doHideCheckboxes;
  const doHideCreatedFor = !!brochureSettings.doHideCreatedFor;
  const doShowEmptyEmailMessage = !!brochureSettings.doShowEmptyEmailMessage;
  const doHideShowPricing = !!brochureSettings.doHideShowPricing;
  const doHideUpdatePricingCheckbox = !!brochureSettings.doHideUpdatePricingCheckbox;
  const defaultUpdatePricing = !!brochureSettings.defaultUpdatePricing;
  const brochures = useSelector((state) => state.brochure.brochures[id] || {});
  const doAllowBookDefaultValue = !!brochureSettings.doAllowBookDefaultValue;
  const allTrips = useMemo(() => Object.values(brochure.trips), [brochure.trips]);
  const isHotelOnly =
    allTrips.length > 0 &&
    allTrips.every((trip) =>
      trip.tripSegments.every((segment) => segment.itinerary.mode === TravelTypes.hotel),
    );
  const doShowPaperTemplate = brochureSettings.doShowPaperTemplate !== false && isHotelOnly;

  const isPaperTemplate = brochure.templateId === BrochureTemplateId.PAPER;

  const handleTemplateChange = useCallback(
    (templateId) => {
      dispatch(updateBrochureField({ fieldName: 'templateId', fieldValue: templateId }));
      if (templateId === BrochureTemplateId.CLASSIC) {
        // Clear paper-template content so the classic save/send flow
        // doesn't accidentally send the paper editorHTML.
        dispatch(
          updateBrochureFields({
            fields: { doShowBrochureEditor: false, editorJSON: '', editorHTML: '' },
          }),
        );
        // Also clear from brochures[id] so the classic Brochure preview
        // doesn't show the TipTap experimental editor with paper content.
        if (brochure.id) {
          dispatch(
            updateStoredBrochureFields({
              brochureId: brochure.id,
              fields: { editorJSON: '', editorHTML: '' },
            }),
          );
        }
      }
      track('BrochureEmailForm.templateChange', { templateId });
    },
    [dispatch, track, brochure.id],
  );

  const [showConfirm, setShowConfirm] = useState(false);

  const onSendClick = () => {
    dispatch(
      sendBrochureRequest({
        ...brochure,
        brochureId: tripId,
        savedBrochureId: brochure.id,
      }),
    );
    track('BrochureEmailForm.onSendClick');
  };

  const hasIncompleteFlightSegments = useMemo(
    () =>
      allTrips.some((trip) =>
        trip.tripSegments.some((segment) => segment.itinerary.isIncompleteItinerary),
      ),
    [allTrips],
  );

  // Remove invalid flights from brochure
  const validTrips = useMemo(
    () =>
      allTrips
        .map((trip) => ({
          ...trip,
          tripSegments: trip.tripSegments.filter(
            (segment) => !segment.itinerary.isIncompleteItinerary,
          ),
        }))
        .filter((trip) => trip.tripSegments.length > 0),
    [allTrips],
  );

  const hasAnyTripSegments = validTrips && validTrips.length;

  const handleSaveBrochure = () => {
    if (!hasAnyTripSegments) return;
    dispatch(
      updateBrochureField({
        fieldName: 'saveBrochureStatus',
        fieldValue: SearchStatus.SEARCHING,
      }),
    );
    dispatch(
      saveBrochureRequest({
        ...{ ...brochure, trips: validTrips },
        brochureId: brochureIdFromUrl ?? tripId,
        savedBrochureId: brochureIdFromUrl ?? brochure.id,
        resultsPageUrl: removeFlightSelectionParams(window.location.href),
        doSaveToDatabase: true,
      }),
    );
    track('BrochureEmailForm.onSavePreviewClick');
  };

  const onSavePreviewClick = () => {
    if (hasIncompleteFlightSegments) {
      setShowConfirm(true);
      return;
    }
    handleSaveBrochure();
  };

  const handleInputChange = (event) => {
    let fieldName = event.target.name;
    let fieldValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    // if (fieldName === 'emailMessage' && brochure.brochureQAs) {
    //   fieldName = 'brochureQAs';
    // }
    dispatch(
      updateBrochureField({
        brochureId: tripId,
        fieldName,
        fieldValue,
      }),
    );
    // Enabling self-booking requires pricing to be visible in the booking flow
    if (fieldName === 'doAllowBook' && fieldValue === true) {
      dispatch(
        updateBrochureField({
          brochureId: tripId,
          fieldName: 'doShowPricing',
          fieldValue: true,
        }),
      );
    }
    // TODO: are we storing both emailRecipientsString
    // and emailRecipients in redux? why? refactor
    if (fieldName === 'emailRecipientsString') {
      fieldName = 'emailRecipients';
      fieldValue = _.map((fieldValue || '').split(','), (e) => e.trim());
      dispatch(
        updateBrochureField({
          brochureId: tripId,
          fieldName,
          fieldValue,
        }),
      );
    }
  };

  const getDefaultEmailMessage = useCallback(
    (agentName, uniqueModesStr) => {
      if (doShowEmptyEmailMessage) return '';
      return travelAgency.defaultBrochureText
        ? travelAgency.defaultBrochureText
            .replace('{{agentName}}', agentName)
            .replace('{{itineraries}}', uniqueModesStr)
        : `Hello,

Here is a ${displayLabel} with some ${uniqueModesStr} options for you to review.

Please let me know if you would like to proceed or have any questions.

Regards,
${agentName}`;
    },
    [displayLabel, doShowEmptyEmailMessage, travelAgency.defaultBrochureText],
  );

  useEffect(() => {
    if (!brochure.isSavedToDatabase && !brochureIdFromUrl) {
      const uniqModes = _.uniq(
        _.map(
          _.flatMap(Object.values(brochure.trips), (t) => t.tripSegments),
          'itinerary.mode',
        ),
      );
      let uniqueModesStr = '';
      _.each(uniqModes, (m, index) => {
        uniqueModesStr += m === TravelTypes.carrental ? 'rental car' : Object.keys(TravelTypes)[m];
        if (index < uniqModes.length - 2) {
          uniqueModesStr += ', ';
        } else if (index < uniqModes.length - 1) {
          uniqueModesStr += ' and ';
        }
      });
      const agentName = name || email.split('@')[0];
      dispatch(
        updateBrochureField({ brochureId: tripId, fieldName: 'brochureQAs', fieldValue: '' }),
      );
      dispatch(
        updateBrochureField({
          brochureId: tripId,
          fieldName: 'emailMessage',
          fieldValue: getDefaultEmailMessage(agentName, uniqueModesStr),
        }),
      );
    }
  }, [
    brochure.isSavedToDatabase,
    brochure.trips,
    dispatch,
    email,
    name,
    brochureIdFromUrl,
    travelAgency.defaultBrochureText,
    tripId,
    displayLabel,
    getDefaultEmailMessage,
  ]);

  const resolveBrochureDefaults = () => {
    const doAllowBook = brochure?.doAllowBook ?? doAllowBookDefaultValue;

    // Self-booking shows prices in the booking flow, so pricing must be on
    const doShowPricing = doAllowBook
      ? true
      : (brochure?.doShowPricing ?? travelAgency?.defaultDoShowPricingToAnonymous ?? true);

    return {
      doAllowBook,
      doShowPricing,
      doUpdatePriceOnPageLoad: brochure?.doUpdatePriceOnPageLoad ?? defaultUpdatePricing,
    };
  };

  useEffect(() => {
    let emailRecipientsString = '';

    if (brochure && brochure.emailRecipients && brochure.emailRecipients.length > 0) {
      emailRecipientsString = brochure.emailRecipients.join(',');
    } else if (brochures && brochures.emailRecipients && brochures.emailRecipients.length > 0) {
      emailRecipientsString = brochures.emailRecipients.join(',');
    }

    dispatch(
      updateBrochureFields({
        brochureId: brochureIdFromUrl ?? tripId,
        fields: {
          ...brochure,
          fromEmail: email,
          saveBrochureError: null,
          sendBrochureError: null,
          saveBrochureStatus: SearchStatus.INITIAL,
          sendBrochureStatus: SearchStatus.INITIAL,
          ...resolveBrochureDefaults(),
          emailRecipientsString,
          doShowBrochureEditor:
            (brochures?.editorHTML && brochure?.templateId === BrochureTemplateId.PAPER) || false,
        },
      }),
    );
    // oxlint-disable-next-line react/exhaustive-deps
  }, [dispatch, tripId, email]);

  useEffect(() => {
    if (isPaperTemplate && !isHotelOnly) {
      handleTemplateChange('classic');
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isHotelOnly]);

  useEffect(() => {
    if (_.isEmpty(brochure?.trips)) return;
    dispatch(
      saveBrochureRequest({
        ...brochure,
        // `brochure` here is from before the init effect's dispatch, so defaults aren't in it yet
        ...resolveBrochureDefaults(),
        brochureId: brochureIdFromUrl ?? tripId,
        savedBrochureId: brochureIdFromUrl ?? brochure.id,
        doSaveToDatabase: false,
      }),
    );
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  const templateSelector = doShowPaperTemplate ? (
    <div className="mt-4 mb-1">
      <p className="text-sm font-medium text-gray-700 mb-2">Template</p>
      <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
        <button
          type="button"
          onClick={() => handleTemplateChange(BrochureTemplateId.CLASSIC)}
          className={`px-4 py-2 text-sm ${!isPaperTemplate ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          Classic
        </button>
        <button
          type="button"
          onClick={() => handleTemplateChange(BrochureTemplateId.PAPER)}
          className={`px-4 py-2 text-sm border-l border-gray-300 ${isPaperTemplate ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          Paper{' '}
          <span className="ml-1 text-xs font-semibold uppercase tracking-wide bg-yellow-500 text-yellow-900 px-2 py-1 rounded">
            Beta
          </span>
        </button>
      </div>
      {isPaperTemplate && <PaperTemplateBanner />}
    </div>
  ) : null;

  return (
    <div>
      <EmailToClientForm
        brochure={brochure}
        createdFor={doHideCreatedFor ? undefined : brochure.createdFor || ''}
        fromEmail={brochure.fromEmail || ''}
        emailRecipientsString={brochure.emailRecipientsString || ''}
        emailSubject={brochure.emailSubject || ''}
        doSendCopyToAgent={brochure.doSendCopyToAgent || false}
        doSendCopyToAgentDisabled={isPaperTemplate}
        doAllowBook={brochure.doAllowBook || false}
        doAllowBookDisabled={isPaperTemplate}
        doShowPricing={brochure.doShowPricing}
        doShowPricingDisabled={brochure.doAllowBook || false}
        customCheckboxes={
          doHideUpdatePricingCheckbox ? (
            <></>
          ) : (
            <>
              <Checkbox
                checked={brochure.doUpdatePriceOnPageLoad}
                name="doUpdatePriceOnPageLoad"
                onChange={handleInputChange}
                value={brochure.doUpdatePriceOnPageLoad}
                label={`Update prices whenever customer opens ${displayLabel}`}
                disabled={isPaperTemplate}
              />
              {isPaperTemplate && (
                <p className="text-xs text-yellow-700 ml-6">Not available in Paper template</p>
              )}
            </>
          )
        }
        onInputChange={handleInputChange}
        doEnableSave={
          saveBrochureStatus !== SearchStatus.SEARCHING &&
          sendBrochureStatus !== SearchStatus.SEARCHING
        }
        onSavePreviewClick={onSavePreviewClick}
        saveStatus={saveBrochureStatus}
        saveError={saveBrochureError}
        saveLabel={id ? `Update ${displayLabel}` : `Save ${displayLabel}`}
        doEnableSend={
          !isPaperTemplate &&
          saveBrochureStatus !== SearchStatus.SEARCHING &&
          brochure &&
          brochure.isSavedToDatabase &&
          sendBrochureStatus !== SearchStatus.SEARCHING
        }
        doEnableCopyLink={
          saveBrochureStatus !== SearchStatus.SEARCHING &&
          brochure &&
          brochure.isSavedToDatabase &&
          sendBrochureStatus !== SearchStatus.SEARCHING
        }
        onSendClick={onSendClick}
        sendStatus={sendBrochureStatus}
        sendError={sendBrochureError}
        sendLabel={`Send ${displayLabel}`}
        emailMessage={brochure.emailMessage}
        doHideSentCopyToMe={doHideSentCopyToMe}
        doHideShowPricing={doHideShowPricing}
        doHideActionButtons={isPaperTemplate}
      >
        {templateSelector}
      </EmailToClientForm>
      {showConfirm && (
        <ConfirmModal
          className="modal-safe-click-zone"
          open={showConfirm}
          onConfirm={() => {
            setShowConfirm(false);
            handleSaveBrochure();
          }}
          onCancel={() => setShowConfirm(false)}
          okButtonLabel={id ? 'Update' : 'Save'}
          disableOkButton={!hasAnyTripSegments}
          message={
            !hasAnyTripSegments ? (
              'There are no valid trip segments to save. Please add the missing segments.'
            ) : (
              <div>
                <div className="mb-2">
                  <span role="img" aria-label="warning">
                    ⚠️
                  </span>{' '}
                  <b>Some flight options are incomplete.</b>
                </div>
                The return (inbound) segments are missing. These incomplete options will be removed
                before saving. Do you want to continue?
              </div>
            )
          }
        />
      )}
    </div>
  );
};

BrochureEmailForm.propTypes = {
  tripId: PropTypes.string,
  id: PropTypes.string,
};

BrochureEmailForm.defaultProps = {
  tripId: null,
  id: null,
};

export default BrochureEmailForm;
